/**
 * Validation and routing helpers for the b2bportal native WebView
 * entry (`/auth/startsession/?code=…&redirect_uri=…`). [NU-470]
 *
 * The Nubank native app funnels every post-booking deep link through
 * the startsession wrapper; `redirect_uri` is the only part of that
 * URL an attacker could meaningfully control, so it must never be
 * navigated to unvalidated (open-redirect hardening). On top of the
 * structural validation, this module recognizes the flight
 * post-booking action shape `/trips/actions/{orderId}/{action}`
 * (the contract path native uses for CFAR / DAFAR / cancel entry),
 * which the auth flow routes to the commerce portal or the legacy
 * b2b flow depending on `CommercePlatformNubankFlightsRollout`.
 *
 * Sibling validators: {@link isSafeCommerceReturnPath} in
 * `nupayCommerceCheckoutPath.ts` applies similar structural rules
 * plus a stays/air prefix allowlist for the NuPay OAuth return path
 * (it lacks this file's percent-decoded re-check and compensates via
 * that allowlist). The overlap is deliberate rather than factored
 * out: the NuPay validator is byte-mirrored into commerce-portals
 * (`libs/checkout/.../nubank/commerceReturnContext.ts`), so hanging
 * it off a b2bportal-local primitive would break the mirror.
 */

import { getDatadogUserId } from "@hopper-b2b/datadog";

import { PATH_TRIPS } from "./urlPaths";

/** Flight post-booking actions supported by the native entry contract. */
export const FLIGHT_EXERCISE_ACTIONS = ["cfar", "dafar", "cancel"] as const;

export type FlightExerciseAction = (typeof FLIGHT_EXERCISE_ACTIONS)[number];

export type FlightActionEntry = {
  orderId: string;
  action: FlightExerciseAction;
};

/** Fixed base URL for parsing relative paths without touching window.location. */
const RELATIVE_PATH_VALIDATION_BASE = "https://nubank.local";

/**
 * Validates a `redirect_uri` query-param value before the post-login
 * navigation. Accepts only a same-origin relative path beginning with
 * a single slash; rejects protocol-relative URLs (`//evil.com`),
 * schemes/colons, credentials markers, backslashes, traversal, and
 * percent-encoded traversal (checked on the percent-decoded value,
 * since WHATWG URL resolution silently *normalizes* `%2e%2e` segments
 * away and the resolved pathname alone cannot reveal them).
 *
 * Deliberately structural rather than prefix-allowlisted: production
 * `redirect_uri` traffic spans many product surfaces (`/flights/…`,
 * `/hotels/…`, `/trips/…`), and the open-redirect guarantee comes
 * from never leaving the origin, not from enumerating pages.
 */
export function isSafePostLoginRedirectPath(rawPath: string): boolean {
  if (!rawPath.startsWith("/") || rawPath.startsWith("//")) {
    return false;
  }
  if (rawPath.includes("..") || rawPath.includes("\\")) {
    return false;
  }
  if (rawPath.includes(":") || rawPath.includes("@")) {
    return false;
  }

  // Percent-decoded re-check: `%2e%2e`, `%2F%2F`, and friends (any
  // casing) must not smuggle traversal or separators past the
  // raw-string checks above. The caller navigates with the RAW
  // value, and URL resolution would normalize the encoded dots
  // straight into a parent-segment pop. A value that fails to decode
  // is malformed input, reject it. Scheme/credential markers are only
  // re-checked in the decoded PATH portion so that percent-encoded
  // query values (e.g. `?time=12%3A30`) stay accepted.
  let decoded: string;
  try {
    decoded = decodeURIComponent(rawPath);
  } catch {
    return false;
  }
  if (decoded.includes("..") || decoded.includes("\\")) {
    return false;
  }
  if (decoded.startsWith("//")) {
    return false;
  }
  const decodedPathPortion = decoded.split(/[?#]/, 1)[0];
  if (decodedPathPortion.includes(":") || decodedPathPortion.includes("@")) {
    return false;
  }

  let resolved: URL;
  try {
    resolved = new URL(rawPath, RELATIVE_PATH_VALIDATION_BASE);
  } catch {
    return false;
  }

  // Re-check the *resolved* URL: catches encoded traversal that the
  // raw-string checks above can't see, and any parser quirk that
  // escapes the fixed validation origin.
  if (resolved.origin !== RELATIVE_PATH_VALIDATION_BASE) {
    return false;
  }
  if (
    resolved.pathname.includes("..") ||
    !resolved.pathname.startsWith("/") ||
    resolved.pathname.startsWith("//")
  ) {
    return false;
  }

  return true;
}

/**
 * Prefix of the native flight action entry shape. Lets callers tell
 * a *malformed* action path (action-shaped but failing
 * {@link parseFlightActionEntryPath}) apart from an ordinary
 * redirect, so it can fail to a safe known page instead of a
 * half-matched trips route.
 */
export const FLIGHT_ACTION_ENTRY_PREFIX = `${PATH_TRIPS}actions/`;

/**
 * Loose shape test used for the malformed-deep-link guard. Matched
 * case-insensitively and with or without the trailing slash because
 * React Router matches routes case-insensitively: `/trips/Actions/x`
 * or `/trips/actions` would otherwise slip past a strict prefix
 * check and land inside the `/trips/*` wildcard as a garbage route.
 * The strict, case-sensitive contract shape remains
 * {@link parseFlightActionEntryPath}.
 */
export function isFlightActionEntryShapedPath(path: string): boolean {
  const lowered = path.toLowerCase();
  return (
    lowered.startsWith(FLIGHT_ACTION_ENTRY_PREFIX) ||
    lowered === FLIGHT_ACTION_ENTRY_PREFIX.slice(0, -1)
  );
}

/**
 * Reduces a caller-controlled redirect value to log-safe metadata.
 * Prior reviews on the auth flow (NU-18 SEC-002, NuPay SEC-001)
 * established that raw redirect URLs must never be logged: their
 * query strings can carry OAuth codes or state. Only the path
 * portion, truncated, is safe to emit.
 */
export function toLogSafeRedirectPath(rawPath: string): string {
  return rawPath.split(/[?#]/, 1)[0].slice(0, 60);
}

const FLIGHT_WATCH_QUERY_PARAM_KEYS = [
  "origin",
  "destination",
  "originType",
  "destinationType",
  "departureDate",
  "returnDate",
  "tripCategory",
  "isFromFlightWatch",
  "stopsOption",
  "noLCC",
  "adultsCount",
  "childrenCount",
  "infantsInSeatCount",
  "infantsOnLapCount",
] as const;

export type PriceWatchRedirectEntrySource =
  | "watch_flag"
  | "deeplink_without_watch_flag"
  | "organic";

function getRedirectEntrySource(
  keys: readonly string[]
): PriceWatchRedirectEntrySource {
  if (keys.includes("isFromFlightWatch")) {
    return "watch_flag";
  }
  if (
    keys.includes("origin") &&
    keys.includes("destination") &&
    keys.includes("departureDate")
  ) {
    return "deeplink_without_watch_flag";
  }
  return "organic";
}

/**
 * Describes a native post-login redirect without logging query values.
 *
 * Query strings may contain OAuth codes or state, so diagnostics expose only
 * the total key count and an allowlisted subset used by flight-watch links.
 */
export function getLogSafeRedirectMetadata(rawPath: string | null) {
  if (!rawPath) {
    return {
      hasRedirectUri: false,
      redirectPath: null,
      queryParamCount: 0,
      flightWatchQueryParamKeys: "",
      entrySource: "organic" as PriceWatchRedirectEntrySource,
      userId: getDatadogUserId(),
      diagnostic: "NVH-661",
      diagnostics: "NVH-661,NVH-1070",
    };
  }

  const query = rawPath.split("?", 2)[1]?.split("#", 1)[0] ?? "";
  const queryParams = new URLSearchParams(query);
  const presentFlightWatchKeys = FLIGHT_WATCH_QUERY_PARAM_KEYS.filter((key) =>
    queryParams.has(key)
  );

  return {
    hasRedirectUri: true,
    redirectPath: toLogSafeRedirectPath(rawPath),
    queryParamCount: Array.from(queryParams.keys()).length,
    flightWatchQueryParamKeys: presentFlightWatchKeys.join(","),
    entrySource: getRedirectEntrySource(presentFlightWatchKeys),
    userId: getDatadogUserId(),
    diagnostic: "NVH-661",
    diagnostics: "NVH-661,NVH-1070",
  };
}

/**
 * `/trips/actions/{orderId}/{action}`, the native flight action
 * entry shape from the Commerce Portal Contract. `orderId` is the
 * orders-api tripId (≡ the legacy `bookedItinerary.id`), matched
 * conservatively as an unencoded id token; `{action}` must be one of
 * {@link FLIGHT_EXERCISE_ACTIONS}, lowercase, per the contract.
 * An optional trailing slash is tolerated; query strings and extra
 * segments are not: an unexpected shape falls through to the plain
 * redirect handling, which lands on a safe non-action page.
 */
const FLIGHT_ACTION_ENTRY_PATTERN =
  /^\/trips\/actions\/([A-Za-z0-9][A-Za-z0-9._-]*)\/(cfar|dafar|cancel)\/?$/;

/**
 * Parses a validated redirect path into a flight action entry, or
 * `null` when the path is not the action shape (callers then treat
 * it as a plain post-login redirect). Call
 * {@link isSafePostLoginRedirectPath} first; this parser assumes a
 * structurally safe path.
 */
export function parseFlightActionEntryPath(
  path: string
): FlightActionEntry | null {
  const match = FLIGHT_ACTION_ENTRY_PATTERN.exec(path);
  if (!match) {
    return null;
  }
  const [, orderId, action] = match;
  return { orderId, action: action as FlightExerciseAction };
}

/**
 * Maps a flight action entry to the supported legacy b2bportal flow,
 * used when `CommercePlatformNubankFlightsRollout` is OFF for the
 * user. All targets must survive a COLD WebView entry (fresh page
 * load straight after auth, no Redux state):
 *
 * - `cancel` and `cfar` → `/trips/?tripId={orderId}&cancel`. The
 *   `tripId` deeplink hydrates and auto-opens the flight's details
 *   in the UberTripsList surface Nubank renders (`mytrips-list` slot
 *   override in `Body/index.tsx`), and its details component
 *   auto-opens the self-serve cancel modal on the `cancel` param.
 *   For a CFAR-contract flight the cancel quote routes into the
 *   CFAR exercise flow, so this single entry serves both actions.
 *   The direct `/trips/{orderId}/cfar` route exists but reads
 *   `getSelectedFlight` from Redux, which only the trips list
 *   populates, so a cold entry there bounces to a bare list.
 * - `dafar`  → `/trips/disruption/exercise/{orderId}`, the
 *   disruption exercise route in `Body/index.tsx`. `DisruptionApp`
 *   fetches its own itinerary (it already serves cold email
 *   deeplinks), and the route's completion handler treats
 *   `:reservationId` as the tripId.
 */
export function buildLegacyFlightActionPath(entry: FlightActionEntry): string {
  const orderId = encodeURIComponent(entry.orderId);
  switch (entry.action) {
    case "cfar":
    case "cancel":
      return `${PATH_TRIPS}?tripId=${orderId}&cancel`;
    case "dafar":
      return `${PATH_TRIPS}disruption/exercise/${orderId}`;
  }
}
